import {
  Box,
  Button,
  Center,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spacer,
  Stack,
  useDisclosure,
} from '@chakra-ui/react';
import { DateRangeOutlined, ExpandMoreOutlined } from '@mui/icons-material';
import { format as formatDate } from 'date-fns';
import { useEffect, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { useFormContext } from 'react-hook-form';

import { RangeDatePicker } from '../date-picker/range-date/range-date';
import { Modal } from '../modal/modal';

interface DateFilterProps {
  fromField?: string;
  toField?: string;
  buttonVariant?: string;
  buttonName?: string;
  shortYearFormat?: boolean;
  dateFormat?: string;
  onFilterChange?: (periodType: string) => { fromDate: string; toDate: string };
  onCustomDateChange?: (fromDate: string, toDate: string) => void;
}

export function DateFilter({
  fromField = 'order_date-from',
  toField = 'order_date-to',
  buttonVariant = 'solid',
  buttonName = 'Pilih Tanggal',
  shortYearFormat = false,
  dateFormat = 'dd/MM/yyyy',
  onFilterChange,
  onCustomDateChange,
}: DateFilterProps) {
  const date = useDisclosure();
  const { setValue, watch } = useFormContext();
  const selectedDateRangeState = useState<DateRange | undefined>();
  const [selectedDateRange, setSelectedDateRange] = selectedDateRangeState;

  /**
   * Handles selection of predefined date periods
   * Updates form values and triggers callbacks when a period is selected
   */
  const handleFilterPeriod = (period: string) => {
    if (onFilterChange) {
      const { fromDate, toDate } = onFilterChange(period);
      setValue(fromField, fromDate, { shouldDirty: true });
      setValue(toField, toDate, { shouldDirty: true });
      setSelectedDateRange(undefined);
      if (onCustomDateChange) {
        onCustomDateChange(fromDate, toDate);
      }
    }
  };

  /**
   * Formats the date range for display in the button
   * Handles shortening year format if shortYearFormat is true
   */
  const formatDateDisplay = () => {
    const fromDate = watch(fromField);
    const toDate = watch(toField);

    if (!fromDate || !toDate) return buttonName;

    if (shortYearFormat) {
      const shortFromDate = fromDate.replace(
        /\/(\d{4})$/,
        (match: string) => match[0] + match.slice(-2)
      );
      const shortToDate = toDate.replace(
        /\/(\d{4})$/,
        (match: string) => match[0] + match.slice(-2)
      );
      return `${shortFromDate} s/d ${shortToDate}`;
    } else {
      return `${fromDate} s/d ${toDate}`;
    }
  };

  /**
   * Synchronizes the internal date picker state with form fields
   * Updates form values when the date range picker selection changes
   */
  useEffect(() => {
    const range = selectedDateRange;
    let newFromValue = '';
    let newToValue = '';

    if (range?.from) {
      newFromValue = formatDate(range.from, dateFormat);
    }
    if (range?.to) {
      newToValue = formatDate(range.to, dateFormat);
    }

    const currentFromValue = watch(fromField);
    const currentToValue = watch(toField);

    if (currentFromValue !== newFromValue) {
      setValue(fromField, newFromValue, { shouldDirty: true });
    }
    if (currentToValue !== newToValue) {
      setValue(toField, newToValue, { shouldDirty: true });
    }
  }, [selectedDateRange, fromField, toField, setValue, dateFormat, watch]);

  /**
   * Triggers the external callback when form date values change
   * Only calls onCustomDateChange when both dates are set or both are empty
   */
  const watchedFromDate = watch(fromField);
  const watchedToDate = watch(toField);
  useEffect(() => {
    if (onCustomDateChange) {
      if (
        (watchedFromDate && watchedToDate) ||
        (!watchedFromDate && !watchedToDate)
      ) {
        onCustomDateChange(watchedFromDate || '', watchedToDate || '');
      }
    }
  }, [watchedFromDate, watchedToDate, onCustomDateChange]);

  const internalPickerName = 'date_filter_internal_picker';

  return (
    <>
      <Box display={{ base: 'none', md: 'flex' }} zIndex="100">
        <Menu>
          <MenuButton
            as={Button}
            variant={buttonVariant}
            leftIcon={<DateRangeOutlined />}
            rightIcon={<ExpandMoreOutlined />}
            alignContent="center"
          >
            {formatDateDisplay()}
          </MenuButton>

          <MenuList zIndex={2000}>
            <MenuItem onClick={() => handleFilterPeriod('all')}>Semua</MenuItem>
            <MenuItem onClick={() => handleFilterPeriod('7d')}>
              7 Hari Terakhir
            </MenuItem>
            <MenuItem onClick={() => handleFilterPeriod('1m')}>
              1 Bulan Terakhir
            </MenuItem>
            <MenuItem onClick={() => handleFilterPeriod('3m')}>
              3 Bulan Terakhir
            </MenuItem>
            <MenuItem onClick={() => handleFilterPeriod('6m')}>
              6 Bulan Terakhir
            </MenuItem>
            <MenuItem onClick={() => handleFilterPeriod('1y')}>
              1 Tahun Terakhir
            </MenuItem>
            <MenuItem onClick={date.onOpen}>Pilih Tanggal</MenuItem>
          </MenuList>
        </Menu>
      </Box>

      <Modal
        size="2xl"
        title="Pilih Tanggal"
        name="date_filter_modal"
        isOpen={date.isOpen}
        onClose={date.onClose}
        onSubmit={date.onClose}
        zIndex={2000}
      >
        <Center>
          <Stack>
            <Flex>
              <Spacer />
              <Button
                color="red"
                variant="link"
                border="none"
                fontWeight="normal"
                onClick={() => {
                  setSelectedDateRange(undefined);
                  setValue(fromField, '', { shouldDirty: true });
                  setValue(toField, '', { shouldDirty: true });
                  setValue(`${internalPickerName}-from`, '', {
                    shouldDirty: false,
                  });
                  setValue(`${internalPickerName}-to`, '', {
                    shouldDirty: false,
                  });
                }}
              >
                Hapus
              </Button>
            </Flex>
            <RangeDatePicker
              selectedDateRangeState={selectedDateRangeState}
              name="date_filter_internal_picker"
              format={dateFormat}
              mode="range"
            />
          </Stack>
        </Center>
      </Modal>
    </>
  );
}
